import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { ChatInterfaceState, ChatInterfaceActions } from '@/types/chat';
import { Message, Posit } from '@/types/posit';
import { generateMessageId } from '@/utils/message';
import { useIsMobile } from '@/hooks/use-mobile';

type ChatInterfaceContextType = ChatInterfaceState & ChatInterfaceActions;

const ChatInterfaceContext = createContext<ChatInterfaceContextType | undefined>(undefined);

interface ChatInterfaceProviderProps {
  children: ReactNode;
}

export const ChatInterfaceProvider: React.FC<ChatInterfaceProviderProps> = ({ children }) => {
  const isMobile = useIsMobile();
  
  const [state, setState] = useState<ChatInterfaceState>({
    currentPosit: null,
    currentConversation: null,
    messages: [],
    isLoading: false,
    isStreaming: false,
    sidebarOpen: false, // Start closed, will be updated in useEffect
    welcomeMode: true,
    scrollBehavior: 'auto'
  });

  // Initialize sidebar state - start closed on all devices
  useEffect(() => {
    setState(prev => ({
      ...prev,
      sidebarOpen: false // Start closed on all devices
    }));
  }, []);

  // Handle orientation changes - close sidebar on orientation change to prevent layout issues
  useEffect(() => {
    const handleOrientationChange = () => {
      // Close sidebar on orientation change to prevent layout conflicts
      setState(prev => ({
        ...prev,
        sidebarOpen: false
      }));
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, []);

  const setCurrentPosit = useCallback((posit: Posit | null) => {
    setState(prev => ({
      ...prev,
      currentPosit: posit,
      messages: [], // Clear messages when switching Posits
      welcomeMode: true,
      currentConversation: null
    }));
  }, []);

  const addMessage = useCallback((message: Message) => {
    setState(prev => ({
      ...prev,
      messages: [...prev.messages, message],
      welcomeMode: false
    }));
  }, []);

  const updateMessage = useCallback((id: string, updates: Partial<Message>) => {
    setState(prev => ({
      ...prev,
      messages: prev.messages.map(msg => 
        msg.id === id ? { ...msg, ...updates } : msg
      )
    }));
  }, []);

  const clearMessages = useCallback(() => {
    setState(prev => ({
      ...prev,
      messages: [],
      welcomeMode: true,
      currentConversation: null
    }));
  }, []);

  const toggleSidebar = useCallback(() => {
    setState(prev => ({
      ...prev,
      sidebarOpen: !prev.sidebarOpen
    }));
  }, []);

  const setWelcomeMode = useCallback((enabled: boolean) => {
    setState(prev => ({
      ...prev,
      welcomeMode: enabled
    }));
  }, []);

  const setScrollBehavior = useCallback((behavior: 'auto' | 'manual') => {
    setState(prev => ({
      ...prev,
      scrollBehavior: behavior
    }));
  }, []);

  const setIsLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({
      ...prev,
      isLoading
    }));
  }, []);

  const setIsStreaming = useCallback((isStreaming: boolean) => {
    setState(prev => ({
      ...prev,
      isStreaming
    }));
  }, []);

  const value: ChatInterfaceContextType = {
    ...state,
    setCurrentPosit,
    addMessage,
    updateMessage,
    clearMessages,
    toggleSidebar,
    setWelcomeMode,
    setScrollBehavior,
    setIsLoading,
    setIsStreaming
  };

  return (
    <ChatInterfaceContext.Provider value={value}>
      {children}
    </ChatInterfaceContext.Provider>
  );
};

export const useChatInterface = (): ChatInterfaceContextType => {
  const context = useContext(ChatInterfaceContext);
  if (context === undefined) {
    throw new Error('useChatInterface must be used within a ChatInterfaceProvider');
  }
  return context;
}; 